// ============================================================================
// DEVELOPMENT MODE CONFIGURATION
// This file controls development-only features that should NOT be enabled
// in production (Azure hosted environment)
// ============================================================================

import { User } from '../services/authService';
import { brand } from './brand';

/**
 * When true, bypasses authentication checks in local development.
 * Set to false if you want to test the actual auth flow locally.
 */
export const DEV_BYPASS_AUTH = true;

// Compute these values once at module load time
const _isDev = import.meta.env.DEV === true;
const _hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const _isLocalHost = _hostname === 'localhost' || _hostname === '127.0.0.1' || _hostname.startsWith('192.168.');
const _isLocalDevelopment = _isDev && _isLocalHost;
const _shouldBypassAuth = _isLocalDevelopment && DEV_BYPASS_AUTH;

// Log once on module load
console.log('[DevMode] Configuration:', {
  isDev: _isDev,
  hostname: _hostname,
  isLocalHost: _isLocalHost,
  isLocalDevelopment: _isLocalDevelopment,
  DEV_BYPASS_AUTH,
  shouldBypassAuth: _shouldBypassAuth,
  mode: import.meta.env.MODE,
});

/**
 * Checks if the app is running in local development mode.
 * Returns true only when running via `npm run dev` on localhost.
 */
export const isLocalDevelopment = (): boolean => _isLocalDevelopment;

/**
 * Combined check: only bypass auth if both conditions are met:
 * 1. Running in local development
 * 2. DEV_BYPASS_AUTH is enabled
 */
export const shouldBypassAuth = (): boolean => _shouldBypassAuth;

/**
 * Mock user for development mode.
 * Used when auth is bypassed to provide user data to components.
 */
export const DEV_MOCK_USER: User = {
  id: 'dev-user-123',
  email: 'dev@localhost.test',
  firstName: 'Dev',
  lastName: 'User',
  company: `${brand.name} Dev Team`,
  accountType: 'developer',
  createdAt: new Date().toISOString(),
};

/**
 * Log a warning when dev features are active.
 * Called once on app initialization.
 */
export const logDevModeWarning = (): void => {
  if (shouldBypassAuth()) {
    console.warn(
      '%c[DEV MODE] Authentication bypass is ENABLED. This should only happen in local development.',
      'background: #ff9800; color: black; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
  }
};
